import { API } from 'aws-amplify';
import React, { useEffect,useState } from 'react';

import NumberFormat, { NumberFormatValues } from 'react-number-format';

import {
  Button,
  Grid,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Switch,
} from '@material-ui/core';

import { getAPIName } from '../config';
import { generateHandleChange } from '../common/state-setter-generator';
import Dealership from '../_models/Dealership';
import { States } from '../_models/states';
import { Timezones } from '../_models/Timezones';
import AutocompleteSelect from './common/AutocompleteSelect';

export interface DealershipFormProps {
  dealershipToEdit?: Dealership;
  handleSubmit(dealership: Dealership): void;
}
export interface lmsversion {
  id?: number;
  lmsname?: string;
}
interface inputChangeState {
  readonly userInput: string;
}

export default function DealershipForm(props: DealershipFormProps) {
  const [lmsversions,setLmsversions] = useState([])
  // primary state ////////////////////////////////////////////////////////////
  const [inputChangeState, setInputChangeState] = React.useState<
    inputChangeState
  >({ userInput: '' });
  const [state, setState] = React.useState<Dealership>({} as Dealership);
  const [feeChecked, setFeeChecked] = React.useState(false);
  const [ccDescriptorErrors, setCcDescriptorErrors] = React.useState<string[]>([]);

  // Validation function for CC descriptor
  const validateCcDescriptor = (value: string) => {
    const errors: string[] = [];
    if (!value) {
      return errors;
    }
    // Check length
    if (value.length < 1) {
      errors.push('Must be at least 1 character long');
    }
    if (value.length > 10) {
      errors.push('Cannot exceed 10 characters');
    }
    // Check for only Latin characters (allow spaces and hyphens)
    if (!/^[a-zA-Z0-9\s\-]*$/.test(value)) {
      errors.push('Only Latin characters allowed');
    }
    // Check for special characters not allowed
    const forbiddenChars = ['<', '>', '\\', "'", '"', '*'];
    if (forbiddenChars.some(char => value.includes(char))) {
      errors.push('Cannot contain special characters: < > \\ \' " *');
    }
    // Check that it contains at least one letter
    if (!/[a-zA-Z]/.test(value)) {
      errors.push('Must contain at least one letter');
    }
    setCcDescriptorErrors(errors);
    return errors;
  };

  useEffect(() => {
    if (props.dealershipToEdit != null) {
      setState(props.dealershipToEdit);

      if (props.dealershipToEdit.tollFeeType) {
        setFeeChecked(true);
      } else {
        setFeeChecked(false);
      }
      // Validate CC descriptor if it exists
      if (props.dealershipToEdit.statementdescriptor) {
        validateCcDescriptor(props.dealershipToEdit.statementdescriptor);
      }
    } else {
      setState({} as Dealership);
      setCcDescriptorErrors([]);
    }
  }, [props.dealershipToEdit]);

  // fetch stuff //////////////////////////////////////////////////////////////
  const getLmsversions = async() =>{
    try {
      const response: any = await API.get(
        getAPIName(),
        '/api/v1/lms',
        {}
      );
      setLmsversions(response.body);
    } catch (e) {
      console.log(e);
    }
  }
  React.useEffect(() => {
    getLmsversions()
  }, []);

  let firstTime = false;
  React.useEffect(() => {
    if (!feeChecked) {
      setState({ ...state, tollFee: '', tollFeeType: '', tollLineItem: '', tollFeeMax: '', credit: '' });
    }
  }, [feeChecked]);

  // handlers /////////////////////////////////////////////////////////////////
  const handleFeeChange = () => {
    setFeeChecked(!feeChecked);
  };

  const handleChange = generateHandleChange(state, setState);

  const handleCcDescriptorChange = (value: string) => {
    handleChange('statementdescriptor')(value);
    validateCcDescriptor(value);
  };
  const handleDealershipGroupChange = (value: any | any[] | null) => {
    const normalizedGroupId = typeof value === 'number' && Number.isFinite(value) ? value : 0;
    setState({
      ...state,
      dealershipgroupid: normalizedGroupId,
    });
  };
  const feeTypes = ['Percent', 'Flat Rate', 'Daily', 'Transactional','Flat Fee with Credit'];
  // JSX.Element preprocessing /////////////////////////////////////////////////

  const feeTypeItems: JSX.Element[] = feeTypes.map((type: string) => {
    return (
      <MenuItem key={type} value={type}>
        {type}
      </MenuItem>
    );
  });

  const lmsVersionItems: JSX.Element[] = lmsversions.map((e:lmsversion) => (
    <MenuItem key={e.lmsname} value={e.lmsname}>
      {e.lmsname}
    </MenuItem>
  ));

  const stateMenuItems = Object.values(States).map((s) => (
    <MenuItem key={s} value={s}>
      {s}
    </MenuItem>
  ));
  const timezoneMenuItems: JSX.Element[] = Object.values(Timezones).map((t) => (
    <MenuItem key={t} value={t}>
      {t}
    </MenuItem>
  ));

  // return ///////////////////////////////////////////////////////////////////
  return (
    <div className="dealership-profile-form">
      <Grid spacing={2} container alignItems="center">
        <Grid item xs={4} className="label-margin">
          Name
        </Grid>
        <Grid item xs={8}>
          <TextField
            fullWidth
            id="nameField"
            placeholder="Name"
            margin="dense"
            onChange={(event) => handleChange('name')(event.target.value)}
            required
            value={state.name || ''}
          />
        </Grid>
      </Grid>
      <Grid spacing={2} container alignItems="center">
        <Grid item xs={4}>
          Group
        </Grid>
        <Grid item xs={8}>
          <AutocompleteSelect
            apiPath="/dealershipgroups"
            keySelector="id"
            showBreaks={false}
            valueSelector="name"
            value={state.dealershipgroupid || 0}
            onChange={handleDealershipGroupChange}
            placeholder="Select Group"
            styles={{ paddingTop: '0px' }}
            flags={{
              useAPIData: true,
              enableAutocomplete: true,
              sortOptions: true,
            }}
          />
        </Grid>
      </Grid>
      <Grid spacing={2} container alignItems="center">
        <Grid item xs={4}>
          Billing Entity
        </Grid>
        <Grid item xs={8}>
          <TextField
            fullWidth
            id="billing-entity"
            placeholder="Billing Entity"
            // margin="dense"
            onChange={(event) => handleChange('billing_entity')(event.target.value)}
            value={state.billing_entity || state.name || ''}
          />
        </Grid>
      </Grid>
      <Grid spacing={2} container alignItems="center">
        <Grid item xs={4}>
          CC Descriptor
        </Grid>
        <Grid item xs={8}>
          <TextField
            fullWidth
            id="cc-descriptor"
            placeholder="CC Descriptor"
            // margin="dense"
            onChange={(event) => handleCcDescriptorChange(event.target.value)}
            value={state.statementdescriptor || ''}
            error={ccDescriptorErrors.length > 0}
            helperText={ccDescriptorErrors.length > 0 ? ccDescriptorErrors.join(', ') : ''}
          />
        </Grid>
      </Grid>
      <Grid spacing={2} container alignItems="center">
        <Grid item xs={4}>
          Timezone
        </Grid>
        <Grid item xs={8}>
          <Select
            fullWidth
            value={state.timezone || ''}
            onChange={(event: any) =>
              handleChange('timezone')(event.target.value as string)
            }
            margin="dense"
          >
            {timezoneMenuItems}
          </Select>
        </Grid>
      </Grid>
      <Grid spacing={2} container alignItems="center">
        <Grid item xs={4} className="label-margin">
          Address
        </Grid>
        <Grid item xs={8}>
          <TextField
            placeholder="Line 1"
            fullWidth
            id="address"
            margin="dense"
            onChange={(event) =>
              handleChange('addressline1')(event.target.value)
            }
            value={state.addressline1 || ''}
          />
        </Grid>
      </Grid>
      <Grid container justify="flex-end" spacing={2}>
        <Grid item xs={8}>
          <TextField
            placeholder="Line 2"
            fullWidth
            id="address"
            margin="dense"
            onChange={(event) =>
              handleChange('addressline2')(event.target.value)
            }
            value={state.addressline2 || ''}
          />
        </Grid>
      </Grid>
      <Grid spacing={2} container alignItems="center" alignContent="center">
        <Grid item xs={4}>
          City
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            placeholder="City"
            id="city"
            margin="dense"
            onChange={(event) => handleChange('city')(event.target.value)}
            value={state.city || ''}
          />
        </Grid>
        <Grid item xs={2}>
          <FormControl fullWidth margin="dense">
            <Select
              id="state"
              value={state.state || ''}
              // variant="outlined"
              onChange={(event: any) =>
                handleChange('state')(event.target.value)
              }
            >
              {stateMenuItems}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <TextField
            id="zipcode"
            margin="dense"
            onChange={(event) => handleChange('zipcode')(event.target.value)}
            placeholder="Zip"
            value={state.zipcode || ''}
          />
        </Grid>
      </Grid>
      <Grid spacing={2} container alignItems="center">
        <Grid item xs={4}>
          Phone
        </Grid>
        <Grid item xs={8}>
          <NumberFormat
            customInput={TextField}
            format="+1 (###) ###-####"
            mask="_"
            fullWidth
            id="phone"
            margin="dense"
            onValueChange={(values: NumberFormatValues) =>
              handleChange('phone')(values.value)
            }
            placeholder="Phone"
            value={state.phone || ''}
          />
        </Grid>
      </Grid>
      <Grid spacing={2} container alignItems="center">
        <Grid item xs={4}>
          Email
        </Grid>
        <Grid item xs={8}>
          <TextField
            fullWidth
            id="email"
            margin="dense"
            onChange={(event) =>
              handleChange('emailaddress')(event.target.value)
            }
            placeholder="Email"
            value={state.emailaddress || ''}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={4}>
          Account Number
        </Grid>
        <Grid item xs={8}>
          <TextField
            fullWidth
            id="account-number"
            margin="dense"
            onChange={(event) =>
              handleChange('accountnumber')(event.target.value)
            }
            placeholder="Account Number"
            value={state.accountnumber || ''}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={4}>
          Location Code
        </Grid>
        <Grid item xs={8}>
          <TextField
            fullWidth
            id="location-code"
            margin="dense"
            onChange={(event) =>
              handleChange('locationcode')(event.target.value)
            }
            placeholder="Location Code"
            value={state.locationcode || ''}
          />
        </Grid>
      </Grid>
      <Grid spacing={2} container alignItems="center">
        <Grid item xs={4}>
          LMS
        </Grid>
        <Grid item xs={8}>
          <Select
            fullWidth
            margin="dense"
            onChange={(event: any) =>
              handleChange('lmsversion')(event.target.value)
            }
            value={state.lmsversion || 0}
          >
            {lmsVersionItems}
          </Select>
        </Grid>
      </Grid>
      <Grid spacing={2} container alignItems="center">
        <Grid item xs={4}>
          Add Toll Fee
        </Grid>
        <Grid item container xs={8} alignContent="flex-start">
          <Switch
            checked={feeChecked}
            onChange={handleFeeChange}
            name="checked"
            inputProps={{ 'aria-label': 'secondary checkbox' }}
          />
        </Grid>
        {feeChecked && (
          <Grid spacing={2} container>
            <Grid item xs={4}>
              Fee Type
            </Grid>
            <Grid item xs={8}>
              <Select
                fullWidth
                margin="dense"
                onChange={(event: any) =>
                  handleChange('tollFeeType')(event.target.value)
                }
                value={state.tollFeeType}
              >
                {feeTypeItems}
              </Select>
            </Grid>
            <Grid item xs={4}>
            {state.tollFeeType} Fee Amount
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                id="tollFee"
                margin="dense"
                type="number"
                inputProps={{ min: 0 }}
                onChange={(event) =>
                  handleChange('tollFee')(event.target.value)
                }
                placeholder="Toll Fee Amount"
                value={state.tollFee}
              />
            </Grid>
            <Grid item xs={4}>
            {state.tollFeeType} Fee Description
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                id="tollLineItem"
                margin="dense"
                // type="number"
                inputProps={{ min: 0 }}
                onChange={(event) =>
                  handleChange('tollLineItem')(event.target.value)
                }
                placeholder="Toll Fee Description"
                value={state.tollLineItem}
              />
            </Grid>
          </Grid>
        )}
        {feeChecked && (state.tollFeeType === 'Daily' ||  state.tollFeeType === 'Transactional') && (
          <Grid spacing={2} container>
            <Grid item xs={4}>
              {state.tollFeeType} Fee Max
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                id="tollFeeMax"
                margin="dense"
                type="number"
                inputProps={{ min: 0 }}
                onChange={(event) =>
                  handleChange('tollFeeMax')(event.target.value)
                }
                placeholder="Toll Fee Max Amount"
                value={state.tollFeeMax}
              />
            </Grid>
            </Grid>
            )}
          {feeChecked && (state.tollFeeType === 'Flat Fee with Credit') && (
            <Grid spacing={2} container>
              <Grid item xs={4}>
                Credit Amount
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  id="credit"
                  margin="dense"
                  type="number"
                  inputProps={{ min: 0 }}
                  onChange={(event) =>
                    handleChange('credit')(event.target.value)
                  }
                  placeholder="Credit to Customer"
                  value={state.credit}
                />
              </Grid>
            </Grid>
            )}
      </Grid>
      <Grid spacing={2} container alignItems="center">
        <Grid item xs={4}>
          Active Invoice
        </Grid>
        <Grid item container xs={8} alignContent="flex-start">
          <Switch
            checked={state.active_invoice ? state.active_invoice : false}
            onChange= {(event) => handleChange('active_invoice')(event.target.checked)}
            name="checked"
            inputProps={{ 'aria-label': 'secondary checkbox' }}
            value = {state.active_invoice || false}
          />
        </Grid>
      </Grid>
      <Grid spacing={2} container alignItems="center">
        <Grid item xs={4}>
          Active Payment
        </Grid>
        <Grid item container xs={8} alignContent="flex-start">
          <Switch
            checked={state.active_payments ? state.active_payments : false}
            onChange= {(event) => handleChange('active_payments')(event.target.checked)}
            name="checked"
            inputProps={{ 'aria-label': 'secondary checkbox' }}
            value = {state.active_payments || false}
          />
        </Grid>
      </Grid>
      <Grid spacing={2} container alignItems="center" justify="flex-end">
        <Button
          className="blue-button"
          style={{ marginTop: '2rem' }}
          onClick={() => {
            const dealershipToSubmit = {
              ...state,
              billing_entity: state.billing_entity || state.name
            };
            props.handleSubmit(dealershipToSubmit);
          }}
          variant="outlined"
          disabled={ccDescriptorErrors.length > 0}
        >
          Save
        </Button>
      </Grid>
    </div>
  );
}
